import React, { ReactNode, useState, useEffect, useCallback } from "react";
import { ChatMessage } from "@/components/agent/chat/types";
import useFetchConversation from "@/hooks/chat/useFetchConversation";
import { useFetchInternalConversation } from "@/hooks/chat/useFetchInternalConversation";
import { useFetchSpecificInternalConversation } from "@/hooks/chat/useFetchSpecificInternalConversation";
import { useSendMessage } from "@/hooks/chat/useSendMessage";
import { useAgentChatUser } from "@/hooks/chat/useAgentChatUser";
import { useAuth } from "@/contexts/AuthContext";
import { getUserSession } from "@/services/authService";
import {
  ChatContext as ImportedChatContext,
  ChatContextType,
} from "@/hooks/useChatContext";
import { useToast } from "@/hooks/use-toast";
import { useConversationReset } from "@/hooks/chat/useConversationReset";
import { useConversationAutoClose } from "@/hooks/chat/useConversationAutoClose";
import { useConversationClosing } from "@/hooks/chat/useConversationClosing";
import { useConversationReopening } from "@/hooks/chat/useConversationReopening";
import { useConversationState } from "@/hooks/chat/useConversationState";
import { useMessageSending } from "@/hooks/chat/useMessageSending";
import {
  saveUserMessage,
  saveAssistantMessage,
  saveSystemMessage,
} from "@/services/messages";
import { Tables } from "@/types/database.types";
import { chatPersistenceService } from "@/services/chatPersistenceService";
import { supabase } from "@/lib/supabase";
import { checkPlanLimits } from "@/lib/planLimits";

interface ChatProviderProps {
  agent: Tables<"agents">;
  organization?: Tables<"organizations">;
  isPublic?: boolean;
  onError?: (message: string) => void;
  brandColor?: string;
  textColor?: string;
  userInfo?: { name?: string; email?: string } | null;
  selectedConversationId?: string | null;
  children: ReactNode;
}

export const ChatProvider = ({
  agent,
  organization,
  isPublic = false,
  onError,
  brandColor = "#060520",
  textColor = "#ffffff",
  userInfo = null,
  selectedConversationId,
  children,
}: ChatProviderProps) => {
  useAuth(); // Hook needed for auth context
  const { agentChatUser, setAgentChatUser } = useAgentChatUser(
    isPublic,
    userInfo,
    agent,
  );
  const { toast } = useToast();

  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [previousResponseId, setPreviousResponseId] = useState<string | null>(
    null,
  );
  const [isFirstInteraction, setIsFirstInteraction] = useState(true);
  const [hasDisplayedWelcome, setHasDisplayedWelcome] = useState(false);
  const [calendlyMeta, setCalendlyMeta] = useState(null);

  // Use different hooks based on whether this is public or internal
  const externalConversationData = useFetchConversation(null, agent?.id);
  const internalConversationData = useFetchInternalConversation(agent?.id);

  const specificInternalConversationData = useFetchSpecificInternalConversation(
    selectedConversationId,
    agent?.id,
  );

  // Handle different return types for internal vs external conversations
  let fetchedConversation: any = null;
  let initialConversationId: string | null = null;
  let initialMessages: any[] = [];
  let initialPrevResponseId: string | null = null;
  let isFetchLoading: boolean = false;
  let fetchError: string | Error | null = null;

  if (isPublic) {
    // External conversation data structure
    const data = externalConversationData;
    fetchedConversation = data.conversation;
    initialConversationId = data.conversationId;
    initialMessages = data.initialMessages || [];
    initialPrevResponseId = data.previousResponseId;
    isFetchLoading = data.isLoading;
    fetchError = data.error;
  } else if (selectedConversationId) {
    // Specific internal conversation data structure
    const data = specificInternalConversationData;
    fetchedConversation = data.conversation;
    initialConversationId = data.conversationId;
    initialMessages = data.initialMessages || [];
    initialPrevResponseId = data.previousResponseId;
    isFetchLoading = data.isLoading;
    fetchError = data.error;
  } else {
    // General internal conversation data structure (simplified)
    const data = internalConversationData;
    initialConversationId = data.conversationId;
    isFetchLoading = data.isLoading;
    fetchError = data.error;
    // For internal conversations, we'll fetch messages separately
    initialMessages = [];
    initialPrevResponseId = null;
  }

  // If selectedConversationId is provided, use that instead of the fetched conversation
  const [conversationId, setConversationId] = useState<string | null>(
    selectedConversationId || initialConversationId,
  );

  // Update conversationId when selectedConversationId changes
  useEffect(() => {
    if (selectedConversationId) {
      setConversationId(selectedConversationId);
    } else if (initialConversationId) {
      setConversationId(initialConversationId);
    }
  }, [selectedConversationId, initialConversationId]);

  const { createConversationIfNeeded } = useMessageSending(
    agent,
    organization,
    conversationId,
    setConversationId,
    agentChatUser,
    setAgentChatUser,
    isPublic,
  );

  const {
    conversationState,
    setConversationState,
    handleConversationCompletion,
  } = useConversationState(agent, chatMessages, setChatMessages);

  const { mutate: sendMessageMutate, isPending: isSendLoading } =
    useSendMessage();

  // Create state for pagination
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Load more messages function
  const loadMoreMessages = async () => {
    if (!conversationId || isLoadingMore) return;
    setIsLoadingMore(true);

    try {
      const offset = chatMessages.length;
      const { messages, hasMore } = await chatPersistenceService.fetchMessages(
        conversationId,
        50,
        offset,
      );

      if (messages && messages.length > 0) {
        setChatMessages((prev) => [...prev, ...messages]);
        setHasMoreMessages(hasMore);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error("Error loading more messages:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Check if there are more messages when conversation changes
  useEffect(() => {
    if (conversationId) {
      // Check if there are more messages
      const checkForMoreMessages = async () => {
        const { hasMore } = await chatPersistenceService.fetchMessages(
          conversationId,
          1,
          chatMessages.length,
        );
        setHasMoreMessages(hasMore);
      };

      checkForMoreMessages();
    }
  }, [conversationId, chatMessages.length]);

  useEffect(() => {
    if (initialConversationId) {
      setConversationId(initialConversationId);
    }

    if (initialMessages.length > 0) {
      setChatMessages(initialMessages);
      setIsFirstInteraction(false);
    } else if (
      !hasDisplayedWelcome &&
      !isFetchLoading &&
      agent?.welcome_message &&
      ((isPublic && agentChatUser) || !isPublic)
    ) {
      setHasDisplayedWelcome(true);
      const welcomeMsg: ChatMessage = {
        type: "agent",
        content: agent.welcome_message,
        timestamp: new Date().toISOString(),
      };
      setChatMessages([welcomeMsg]);
      setIsFirstInteraction(false);

      // Store the welcome message in memory but DON'T create a conversation yet
      // We'll save it to the database when the user actually responds
    }

    if (initialPrevResponseId) {
      setPreviousResponseId(initialPrevResponseId);
    }
  }, [
    hasDisplayedWelcome,
    isFetchLoading,
    agent?.welcome_message,
    isPublic,
    agentChatUser,
    setHasDisplayedWelcome,
    initialConversationId,
    initialPrevResponseId,
    initialMessages,
    setConversationId,
    setPreviousResponseId,
  ]);

  useEffect(() => {
    if (fetchError && onError) {
      const errorMessage =
        fetchError instanceof Error ? fetchError.message : fetchError;
      onError(errorMessage);
    }
  }, [fetchError, onError]);

  const handleSendMessage = useCallback(
    async (messageText: string) => {
      if (!messageText.trim() || isSendLoading) return;

      // Restrict by plan: check conversations limit
      if (organization?.id) {
        const limitError = await checkPlanLimits(
          organization.id,
          "conversations",
        );
        if (limitError) {
          toast({
            variant: "destructive",
            title: "Plan Limit Reached",
            description: limitError.message,
          });
          return;
        }
      }

      // COMMENTED OUT: Prevent sending messages while Calendly booking is pending
      // With the new simplified tag approach, we no longer need this validation
      // as we're not relying on complex function calling flows
      /*
    if (calendlyMeta) {
      console.warn('[CHAT] Cannot send message while Calendly booking is pending');
      toast({
        variant: 'default',
        title: 'Please complete booking',
        description: 'Finish selecting a time, then continue the chat.'
      });
      return;
    }
    */

      let currentConversationId = conversationId;

      if (!currentConversationId) {
        try {
          // Create the conversation now that the user is actually sending a message
          currentConversationId = await createConversationIfNeeded();
          if (!currentConversationId)
            throw new Error("Failed to init conversation.");
          setConversationId(currentConversationId);

          // If we have a welcome message displayed, save it first before saving the user's message
          const welcomeMessage = chatMessages.find(
            (msg) => msg.type === "agent" && hasDisplayedWelcome,
          );
          if (welcomeMessage) {
            try {
              await saveAssistantMessage(
                currentConversationId,
                welcomeMessage.content,
              );
            } catch (saveErr) {
              console.error(
                "Failed to save welcome message to database:",
                saveErr,
              );
              // Continue even if saving welcome message fails
            }
          }
        } catch (err: unknown) {
          const errorMessage =
            err instanceof Error ? err.message : "An unexpected error occurred";
          toast({
            variant: "destructive",
            title: "Error",
            description: errorMessage,
          });
          return;
        }
      }

      const userMessage: ChatMessage = {
        type: "user",
        content: messageText,
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, userMessage]);

      // Save user message to database
      try {
        await saveUserMessage(currentConversationId, messageText);
      } catch (saveErr) {
        console.error("Failed to save user message to database:", saveErr);
        // Continue with the conversation even if saving fails
      }

      sendMessageMutate(
        {
          agent,
          organization,
          isPublic,
          chatMessages,
          newMessageText: messageText,
          conversationId: currentConversationId,
          previousResponseId,
          agentChatUser,
        },
        {
          onSuccess: async (data) => {
            const assistantMessage: ChatMessage = {
              type: "agent",
              content: data.response,
              timestamp: new Date().toISOString(),
            };
            setChatMessages((prev) => [...prev, assistantMessage]);
            setPreviousResponseId(data.responseId);

            // COMMENTED OUT: Enhanced logging for Calendly metadata - no longer needed with simplified approach
            /*
        console.log("[CAL-DBG] backend calendly field →", data.calendly);
        console.log("[CAL-DBG] calendly field exists?", 'calendly' in data);
        console.log("[CAL-DBG] calendly field type:", data.calendly ? typeof data.calendly : 'null');
        if (data.calendly) {
          console.log("[CAL-DBG] calendly field contents:", JSON.stringify(data.calendly));
        }

        setCalendlyMeta(data.calendly ?? null);
        console.log("[CAL-DBG] calendlyMeta set to:", data.calendly ?? null);
        */

            // Save assistant message to database
            try {
              await saveAssistantMessage(
                currentConversationId,
                data.response,
                data.responseId,
              );
            } catch (saveErr) {
              console.error(
                "Failed to save assistant message to database:",
                saveErr,
              );
              // Continue with the conversation even if saving fails
            }

            // Increment conversations_this_month for the organization
            if (organization?.id) {
              try {
                const { error: updateError } = await supabase.rpc(
                  "increment_organization_counter",
                  {
                    org_id: organization.id,
                    counter_name: "conversations_this_month",
                    increment_by: 1,
                  },
                );
                if (updateError) {
                  throw updateError;
                }
              } catch (err) {
                console.error(
                  "Failed to increment conversations_this_month:",
                  err,
                );
              }
            }

            if (data.conversationState === "completed") {
              handleConversationCompletion(currentConversationId ?? "");
            }
            if (setIsFirstInteraction) setIsFirstInteraction(false);
          },
          onError: async (err) => {
            setChatMessages((prev) => prev.slice(0, -1));

            // Add error message as system message
            const errorMessage = err.message || "An error occurred";
            const systemErrorMsg: ChatMessage = {
              type: "system",
              content: `Error: ${errorMessage}`,
              timestamp: new Date().toISOString(),
            };
            setChatMessages((prev) => [...prev, systemErrorMsg]);

            // Save system error message to database
            try {
              await saveSystemMessage(
                currentConversationId,
                `Error: ${errorMessage}`,
              );
            } catch (saveErr) {
              console.error(
                "Failed to save error message to database:",
                saveErr,
              );
            }

            toast({
              variant: "destructive",
              title: "Error",
              description: err.message,
            });
          },
        },
      );
    },
    [
      agent,
      organization,
      isPublic,
      chatMessages,
      conversationId,
      previousResponseId,
      agentChatUser,
      isSendLoading,
      createConversationIfNeeded,
      sendMessageMutate,
      setPreviousResponseId,
      setIsFirstInteraction,
      handleConversationCompletion,
      setChatMessages,
      toast,
      setConversationId,
    ],
  );

  const { canResetConversation, handleResetConversation: originalHandleReset } =
    useConversationReset(
      agent,
      isPublic,
      isSendLoading || isFetchLoading,
      chatMessages,
      setChatMessages,
      conversationId,
      setConversationId,
      setPreviousResponseId,
      setConversationState,
    );

  // Wrap the reset function to also clear persistence data
  const handleResetConversation = useCallback(() => {
    // Clear persistence data if applicable
    if (isPublic && agent?.public_id) {
      chatPersistenceService.clearConversationData(agent.public_id);
    }

    // Reset all the state
    setConversationId(null);
    setPreviousResponseId(null);
    setIsFirstInteraction(true);
    setHasDisplayedWelcome(false);

    // Clear messages but immediately add welcome message if one exists
    setChatMessages([]);

    // Since this is a complete reset, we'll also reset the conversation state
    setConversationState("open");

    // Call the original reset function for any other cleanup
    originalHandleReset();
  }, [
    originalHandleReset,
    isPublic,
    agent?.public_id,
    setConversationId,
    setPreviousResponseId,
    setIsFirstInteraction,
    setHasDisplayedWelcome,
    setChatMessages,
    setConversationState,
  ]);

  useConversationAutoClose({
    messages: chatMessages,
    conversationId,
    conversationState,
    setConversationState,
  });

  const { handleCloseConversation } = useConversationClosing(
    conversationId,
    setChatMessages,
    setConversationState,
    agent.id,
    onError,
  );

  const { handleReopenConversation } = useConversationReopening(
    conversationId,
    setChatMessages,
    setConversationState,
    onError,
  );

  const isLoading = isSendLoading;
  const isConversationLoading = isFetchLoading;

  // ChatContext state tracking for debugging if needed

  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const debug = localStorage.getItem("debug") === "true";
      setDebugMode(debug);
    }
  }, []);

  const effectiveBrandColor =
    organization?.brand_color || brandColor || "#060520";

  const contextValue: ChatContextType = {
    agent,
    organization,
    isPublic,
    agentChatUser,
    chatMessages,
    setChatMessages,
    isLoading,
    isConversationLoading,
    handleSendMessage,
    conversationId,
    setConversationId,
    conversationState,
    setConversationState,
    handleResetConversation,
    handleCloseConversation,
    handleReopenConversation,
    isFirstInteraction,
    setIsFirstInteraction,
    previousResponseId,
    canResetConversation,
    brandColor: effectiveBrandColor,
    textColor,
    debugMode,
    calendlyMeta,
    setCalendlyMeta,
    hasMoreMessages,
    loadMoreMessages,
    isLoadingMore,
  };

  // Store conversation ID in localStorage when it changes
  useEffect(() => {
    if (conversationId) {
      if (isPublic && agent?.public_id) {
        // Store public conversation ID
        chatPersistenceService.storeConversationId(
          agent.public_id,
          conversationId,
        );
      } else if (!isPublic) {
        // Store internal conversation ID for cross-device persistence
        getUserSession()
          .then((session) => {
            if (session?.user?.id) {
              chatPersistenceService.storeInternalConversationId(
                session.user.id,
                agent.id,
                conversationId,
              );
            }
          })
          .catch((err) => {
            console.error("Failed to store internal conversation ID:", err);
          });
      }
    }
  }, [conversationId, isPublic, agent?.public_id, agent.id]);

  // Initialize conversation state from fetched conversation
  useEffect(() => {
    if (fetchedConversation?.state) {
      setConversationState(fetchedConversation.state);
    }
  }, [fetchedConversation?.state, setConversationState]);

  return (
    <ImportedChatContext.Provider value={contextValue}>
      {children}
    </ImportedChatContext.Provider>
  );
};
